import { useState, useEffect, useRef } from 'react';
import type { KioskService, KioskOperator } from './hooks';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function formatDate(date: string): string {
  return new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatTime(t: string): string {
  const [hh, mm] = t.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  return `${hh % 12 || 12}:${mm.toString().padStart(2, '0')} ${ampm}`;
}

interface Props {
  service: KioskService;
  operator: KioskOperator | null;
  date: string;
  time: string;
  autoResetSeconds: number;
  accentColor: string;
  onReset: () => void;
}

export function KioskSuccess({ service, operator, date, time, autoResetSeconds, accentColor, onReset }: Props) {
  const [countdown, setCountdown] = useState(autoResetSeconds);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); onReset(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [onReset]);

  const progress = countdown / autoResetSeconds;
  const circumference = 2 * Math.PI * 22;
  const dashOffset = circumference * (1 - progress);

  return (
    <div style={{
      padding: '60px 24px', textAlign: 'center', fontFamily: BODY_FONT,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
    }}>
      <style>{`
        @keyframes kiosk-check-in { from { transform: scale(0.3); opacity: 0 } 50% { transform: scale(1.08) } to { transform: scale(1); opacity: 1 } }
      `}</style>

      {/* Animated checkmark */}
      <div style={{
        width: 80, height: 80, borderRadius: 40, background: '#ECFDF3',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'kiosk-check-in 500ms ease',
      }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#107C10" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>

      <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>You're all set!</h2>

      {/* Summary card */}
      <div style={{
        background: '#fff', borderRadius: 14, border: '1px solid #E5E5EA',
        padding: '18px 22px', width: '100%', maxWidth: 320, textAlign: 'left',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>{service.name}</div>
        <div style={{ fontSize: 13, color: '#636366' }}>{operator?.name ?? 'First available'}</div>
        <div style={{ fontSize: 13, color: '#636366' }}>{formatDate(date)} at {formatTime(time)}</div>
      </div>

      <div style={{ fontSize: 14, color: '#636366', marginTop: 4 }}>
        Your barber will confirm your appointment shortly.
      </div>

      {/* Countdown ring */}
      <div style={{ position: 'relative', width: 56, height: 56, marginTop: 12 }}>
        <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="28" cy="28" r="22" fill="none" stroke="#F1F3F5" strokeWidth="3" />
          <circle cx="28" cy="28" r="22" fill="none" stroke={accentColor} strokeWidth="3"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 600, color: '#636366',
        }}>{countdown}</div>
      </div>

      <button onClick={() => { if (timerRef.current) clearInterval(timerRef.current); onReset(); }} style={{
        marginTop: 4, padding: '12px 32px', borderRadius: 12,
        border: `1.5px solid ${accentColor}`, background: '#fff',
        color: accentColor, fontSize: 14, fontWeight: 600,
        fontFamily: BODY_FONT, cursor: 'pointer',
        transition: 'background 150ms ease',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = `${accentColor}08`; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
      >
        Book another appointment
      </button>
    </div>
  );
}
